// Installing KCL Canvas as an app (Settings → Install app): which device this is, the
// browser's own install prompt where there is one (Chrome, Edge, Samsung Internet…), and
// step-by-step instructions everywhere else. Also what's possible today for using KCL Canvas
// as a screensaver – browsers can't register as the system screensaver, so it's guidance
// until native apps exist.

import { useEffect, useState } from 'react';

export type Platform = 'windows' | 'mac' | 'iphone' | 'ipad' | 'android' | 'android-tv' | 'fire-tv' | 'samsung-tv' | 'lg-tv' | 'chromeos' | 'linux' | 'other';
export type Browser = 'edge' | 'chrome' | 'firefox' | 'safari' | 'samsung' | 'silk' | 'other';

export interface Device {
  platform: Platform;
  browser: Browser;
}

export const PLATFORM_NAMES: Record<Platform, string> = {
  windows: 'Windows PC',
  mac: 'Mac',
  iphone: 'iPhone',
  ipad: 'iPad',
  android: 'Android phone or tablet',
  'android-tv': 'Android TV / Google TV',
  'fire-tv': 'Fire TV',
  'samsung-tv': 'Samsung TV',
  'lg-tv': 'LG TV',
  chromeos: 'Chromebook',
  linux: 'Linux computer',
  other: 'This device',
};

export const BROWSER_NAMES: Record<Browser, string> = {
  edge: 'Edge',
  chrome: 'Chrome',
  firefox: 'Firefox',
  safari: 'Safari',
  samsung: 'Samsung Internet',
  silk: 'Silk',
  other: 'your browser',
};

/** Works out the device and browser from the user agent (iPads report themselves as Macs, so touch decides). */
export function detectDevice(ua: string, maxTouchPoints = 0): Device {
  const has = (re: RegExp) => re.test(ua);
  let platform: Platform = 'other';
  if (has(/AFT[A-Z0-9]|Silk.*\bTV\b/)) platform = 'fire-tv';
  else if (has(/Tizen/)) platform = 'samsung-tv';
  else if (has(/Web0S|webOS|NetCast/)) platform = 'lg-tv';
  else if (has(/Android/) && has(/\bTV\b|BRAVIA|GoogleTV|Android ?TV|Chromecast|SMART-TV|AbleSign/i)) platform = 'android-tv';
  else if (has(/iPhone|iPod/)) platform = 'iphone';
  else if (has(/iPad/) || (has(/Macintosh/) && maxTouchPoints > 1)) platform = 'ipad';
  else if (has(/Android/)) platform = 'android';
  else if (has(/CrOS/)) platform = 'chromeos';
  else if (has(/Windows/)) platform = 'windows';
  else if (has(/Macintosh|Mac OS X/)) platform = 'mac';
  else if (has(/Linux/)) platform = 'linux';

  let browser: Browser = 'other';
  if (has(/Edg(e|A|iOS)?\//)) browser = 'edge';
  else if (has(/SamsungBrowser/)) browser = 'samsung';
  else if (has(/Silk/)) browser = 'silk';
  else if (has(/Firefox|FxiOS/)) browser = 'firefox';
  else if (has(/Chrome|CriOS|Chromium/)) browser = 'chrome';
  else if (has(/Safari/)) browser = 'safari';
  return { platform, browser };
}

export const isTv = (p: Platform) => p === 'android-tv' || p === 'fire-tv' || p === 'samsung-tv' || p === 'lg-tv';

export interface Guide {
  title: string;
  steps: string[];
  /** The browser can show its own install prompt here (if it has offered one). */
  prompt?: boolean;
  note?: string;
}

/** How to put KCL Canvas on this device as an app, taskbar/dock icon or home-screen tile. */
export function installGuide({ platform, browser }: Device): Guide {
  const chromium = browser === 'chrome' || browser === 'edge';
  switch (platform) {
    case 'windows':
      if (chromium) {
        return {
          title: 'Install it as a Windows app',
          prompt: true,
          steps: [
            browser === 'edge'
              ? 'Select Install KCL Canvas below – or in Edge’s menu (…) choose Apps → Install this site as an app.'
              : 'Select Install KCL Canvas below – or click the install icon at the right of Chrome’s address bar.',
            'KCL Canvas opens in its own window and is added to the Start menu.',
            'To pin it to the taskbar, right-click its icon on the taskbar and choose Pin to taskbar. For a Start tile, right-click it in Start and choose Pin to Start.',
          ],
        };
      }
      return {
        title: 'Add it to your taskbar',
        steps: [
          `${BROWSER_NAMES[browser]} can’t install websites as apps. Open canvas.kcl.cloud in Microsoft Edge (built into Windows) or Chrome and install it from there.`,
          'Or drag the padlock icon from the address bar to your desktop for a shortcut.',
        ],
      };
    case 'mac':
      if (browser === 'safari') {
        return {
          title: 'Add it to your Dock',
          steps: ['In Safari, choose File → Add to Dock (macOS Sonoma or later).', 'KCL Canvas then opens from the Dock in its own window.'],
        };
      }
      if (chromium) {
        return {
          title: 'Install it as a Mac app',
          prompt: true,
          steps: [
            'Select Install KCL Canvas below – or click the install icon in the address bar.',
            'It’s added to your Applications folder and Launchpad. To keep it in the Dock, right-click its Dock icon → Options → Keep in Dock.',
          ],
        };
      }
      return { title: 'Add it to your Dock', steps: ['Open canvas.kcl.cloud in Safari and choose File → Add to Dock, or install it from Chrome or Edge.'] };
    case 'iphone':
    case 'ipad':
      return {
        title: `Add it to your ${platform === 'ipad' ? 'iPad' : 'iPhone'}’s Home Screen`,
        steps: [
          `Tap the Share button${platform === 'ipad' ? ' (top right)' : ' (at the bottom)'} – in Safari, or in Chrome or Edge on iOS 16.4 or later.`,
          'Choose Add to Home Screen, then Add.',
          'KCL Canvas then opens full screen from its Home Screen icon.',
        ],
      };
    case 'android':
      return {
        title: 'Add it to your Home screen',
        prompt: browser !== 'firefox',
        steps:
          browser === 'samsung'
            ? ['Select Install KCL Canvas below – or open the menu (☰) and choose Add page to → Home screen.']
            : browser === 'firefox'
              ? ['Open the menu (⋮) and choose Add to Home screen (or Install).']
              : ['Select Install KCL Canvas below – or open Chrome’s menu (⋮) and choose Add to Home screen → Install.', 'It opens full screen from its icon, like any app.'],
      };
    case 'chromeos':
      return {
        title: 'Install it on your Chromebook',
        prompt: true,
        steps: ['Select Install KCL Canvas below – or click the install icon in the address bar.', 'It appears in the launcher and can be pinned to the shelf.'],
      };
    case 'linux':
      return chromium
        ? { title: 'Install it as an app', prompt: true, steps: ['Select Install KCL Canvas below – or click the install icon in the address bar.'] }
        : { title: 'Install it as an app', steps: ['Open canvas.kcl.cloud in Chrome, Chromium or Edge to install it as an app.'] };
    case 'fire-tv':
    case 'android-tv':
    case 'samsung-tv':
    case 'lg-tv':
      return {
        title: 'Use it on this TV',
        steps: [
          'TV browsers can’t install websites as apps. KCL Canvas apps for TVs are planned.',
          platform === 'fire-tv'
            ? 'Until then, a signage or kiosk app (for example AbleSign) can open canvas.kcl.cloud full screen whenever the TV starts – or bookmark it in Silk.'
            : 'Until then, bookmark canvas.kcl.cloud in the TV’s browser, or use a signage/kiosk app that opens it full screen at start-up.',
          'Sign the TV in from your phone: Settings → Account → Sign in with your phone.',
        ],
      };
    default:
      return { title: 'Keep it handy', steps: ['Bookmark canvas.kcl.cloud, or open it in Chrome or Edge to install it as an app.'] };
  }
}

/** What's possible today for using KCL Canvas as the device's screensaver. */
export function screensaverGuide(platform: Platform): Guide {
  const planned = 'A KCL Canvas screensaver for this device is planned.';
  switch (platform) {
    case 'windows':
      return {
        title: 'Windows',
        steps: [
          'Windows screensavers have to be programs, so a website can’t be one.',
          'Instead, install KCL Canvas (above), open it full screen (F) and set when it’s on in Settings → Screens → Schedules. Outside those times it lets Windows turn the display off as usual.',
        ],
        note: planned,
      };
    case 'mac':
      return {
        title: 'Mac',
        steps: ['macOS screensavers are apps too. Add KCL Canvas to the Dock (above), open it full screen and use Settings → Screens → Schedules for on/off times.'],
        note: planned,
      };
    case 'android-tv':
    case 'android':
      return {
        title: platform === 'android' ? 'Android tablets' : 'Android TV / Google TV',
        steps: [
          'Android’s screen saver (Settings → Display → Screen saver, or Ambient mode on Google TV) only lists installed apps.',
          'Until the KCL Canvas app is available, keep KCL Canvas open full screen and set on/off times in Settings → Screens → Schedules.',
        ],
        note: planned,
      };
    case 'fire-tv':
      return {
        title: 'Fire TV',
        steps: [
          'Fire TV only allows Amazon’s own screensaver.',
          'Use a signage app such as AbleSign to keep KCL Canvas on screen, with on/off times from Settings → Screens → Schedules.',
        ],
      };
    case 'iphone':
    case 'ipad':
      return {
        title: platform === 'ipad' ? 'iPad' : 'iPhone',
        steps: [
          'Apple doesn’t allow other apps to be the screensaver.',
          'For a wall or desk display: add KCL Canvas to the Home Screen (above), open it, and turn on Guided Access (Settings → Accessibility) to keep it on screen. While it’s charging, set Display & Brightness → Auto-Lock to Never.',
        ],
      };
    case 'samsung-tv':
    case 'lg-tv':
      return { title: 'Smart TVs', steps: ['Samsung and LG TVs don’t allow other screensavers. Keep KCL Canvas open full screen and use schedules for on/off times.'], note: 'Apps for these TVs are planned.' };
    case 'chromeos':
      return { title: 'Chromebook', steps: ['ChromeOS screensavers can only be set by an organisation’s administrator. Install KCL Canvas and use schedules for on/off times.'] };
    default:
      return { title: 'This device', steps: ['Keep KCL Canvas open full screen and set on/off times in Settings → Screens → Schedules.'] };
  }
}

// ---- The browser's install prompt -------------------------------------------------------
// Chrome, Edge and Samsung Internet offer it once the page qualifies; it must be caught
// as soon as the page loads (this module is imported from main.tsx).

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
let justInstalled = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // shown from Settings → Install app instead of a mini bar
    deferred = e as InstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    justInstalled = true;
    notify();
  });
}

/** Running as an installed app (its own window, or from the Home Screen). */
export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const mode = (m: string) => window.matchMedia?.(`(display-mode: ${m})`).matches ?? false;
  const standalone = ['standalone', 'minimal-ui', 'window-controls-overlay'].some(mode);
  // "fullscreen" also matches a browser tab put full screen (F / F11), so it counts only when
  // the page didn't ask for it – i.e. the app was opened full screen from its icon (Android).
  const fullscreenApp = mode('fullscreen') && !document.fullscreenElement;
  return justInstalled || standalone || fullscreenApp || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

export function useInstallPrompt(): { canPrompt: boolean; installed: boolean; install: () => Promise<boolean> } {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  return {
    canPrompt: deferred !== null,
    installed: isInstalled(),
    install: async () => {
      if (!deferred) return false;
      const e = deferred;
      await e.prompt();
      const { outcome } = await e.userChoice;
      deferred = null;
      notify();
      return outcome === 'accepted';
    },
  };
}
